/**
 * Ranking signal — "FK-linked table first at JOIN/FROM".
 *
 * Given the statement's bound aliases and the snapshot FK edges, compute the
 * set of tables connected by an FK edge (either direction) to any bound
 * relation. Resolvers promote those bare-table candidates to tier 1 at
 * from-slots with bound aliases, so the actually-joinable tables sort above
 * unrelated ones — and the bare name sorts above its own `X ON …` FK-join
 * snippets via the shorter-label name tiebreak.
 *
 * Edge applicability (direction handling + schema matching) is delegated to
 * `makeApplicableFksFinder`, the same predicate the FK-join snippet emitters
 * use, so the plain-table promotion and the snippet always agree on what
 * counts as "linked".
 */

import type { AliasBinding, FkEdge, TableMeta } from '@/core/engines';

import { makeApplicableFksFinder } from './fkJoinSnippet';
import { makeFkEndpointMatcher } from './fkEndpointMatch';

/** The structural subset of a resolver snapshot table the signal needs. */
export type FkLinkableTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
};

/**
 * Identity key for a snapshot table — spelling kept, so the case-distinct
 * `orders` / `"Orders"` (PostgreSQL, Oracle quoted names, MySQL with
 * `lower_case_table_names = 0`) are two keys. Database is intentionally
 * excluded: FK edges never span databases in the snapshot, and resolvers key
 * their own lookups on (schema, name). Producers and readers both key a
 * snapshot table through this function.
 */
export function fkLinkedTableKey(t: { name: string; schema?: string }): string {
  return `${t.schema ?? ''}|${t.name}`;
}

/**
 * `resolveBinding` — the dialect resolver's own rule for the table a bound
 * relation names — makes the test relation identity (`fkEndpointMatch.ts`),
 * the same one its FK-join snippets use.
 */
export function computeFkLinkedTableKeys<T extends FkLinkableTable>(
  bindings: readonly AliasBinding[],
  tables: readonly T[],
  fks: readonly FkEdge[],
  resolveBinding?: (binding: AliasBinding) => T | undefined,
): ReadonlySet<string> {
  const out = new Set<string>();
  if (bindings.length === 0 || fks.length === 0) return out;
  const relationBindings = bindings.filter(
    (b) => b.kind !== 'cte' && b.kind !== 'subquery' && b.kind !== 'derived',
  );
  if (relationBindings.length === 0) return out;
  const applicableFks = makeApplicableFksFinder(
    fks,
    resolveBinding ? makeFkEndpointMatcher(tables, resolveBinding) : undefined,
  );
  for (const t of tables) {
    const candidate: TableMeta = {
      name: t.name,
      schema: t.schema,
      database: t.database,
      kind: t.kind === 'view' ? 'view' : 'table',
      columns: [],
    };
    for (const b of relationBindings) {
      if (applicableFks(b, candidate).length > 0) {
        out.add(fkLinkedTableKey(t));
        break;
      }
    }
  }
  return out;
}

/**
 * Buffer-locality table keys (Workstream C ranking signal S4): keys of every
 * table whose name appears as a word anywhere in the buffer. Approximates the
 * specs' "recently typed / statement target" intent — at a relation slot
 * (FROM, TRUNCATE/DROP/ALTER target, trigger ON) the tables this script
 * already works with belong above the rest of the schema.
 *
 * Deliberately scans raw word tokens INCLUDING string-literal contents (a
 * quoted `'ACACT_TRIGGER_TARGET'` audit value is still a reference — oracle
 * script.36 §3 depends on it) and INCLUDING the token at the caret (when the
 * caret sits on an existing statement's target the user is re-editing, that
 * table is exactly the one to surface first — oracle script.38 §1). Tokens
 * may start with #, @ or $ so temp-table names participate.
 */
export function computeBufferReferencedTableKeys(
  text: string,
  tables: readonly FkLinkableTable[],
): ReadonlySet<string> {
  const words = new Set<string>();
  for (const m of text.matchAll(/[#@$]?[A-Za-z_][A-Za-z0-9_$#]*/g)) {
    words.add(m[0].toLowerCase());
  }
  const keys = new Set<string>();
  for (const t of tables) {
    if (words.has(t.name.toLowerCase())) keys.add(fkLinkedTableKey(t));
  }
  return keys;
}

/** Union of two promoted-table key sets (FK-linked ∪ buffer-referenced). */
export function unionTableKeys(
  a: ReadonlySet<string>,
  b: ReadonlySet<string>,
): ReadonlySet<string> {
  if (a.size === 0) return b;
  if (b.size === 0) return a;
  return new Set([...a, ...b]);
}
